import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Home, ArrowRight, Clock, Search, SlidersHorizontal, GitCompare } from 'lucide-react';
import { projects } from '../mock';

const Projects = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    priceRange: 'all',
    bedrooms: 'all',
    location: 'all'
  });

  // Extract unique values for filters
  const statuses = ['all', ...new Set(projects.map(p => p.status))];
  const locations = ['all', ...new Set(projects.map(p => p.location))];

  // Filter projects
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filters.status === 'all' || project.status === filters.status;
    
    const matchesLocation = filters.location === 'all' || project.location === filters.location;
    
    let matchesPrice = true;
    if (filters.priceRange !== 'all') {
      const minPrice = parseInt(project.price.replace(/[^0-9]/g, ''));
      if (filters.priceRange === 'under1m') matchesPrice = minPrice < 1000000;
      else if (filters.priceRange === '1m-2m') matchesPrice = minPrice >= 1000000 && minPrice < 2000000;
      else if (filters.priceRange === '2m-5m') matchesPrice = minPrice >= 2000000 && minPrice < 5000000;
      else if (filters.priceRange === 'over5m') matchesPrice = minPrice >= 5000000;
    }

    let matchesBedrooms = true;
    if (filters.bedrooms !== 'all') {
      if (filters.bedrooms === '1-2') matchesBedrooms = project.bedrooms.includes('1') || project.bedrooms.includes('2');
      else if (filters.bedrooms === '3-4') matchesBedrooms = project.bedrooms.includes('3') || project.bedrooms.includes('4');
      else if (filters.bedrooms === '5+') matchesBedrooms = project.bedrooms.includes('5') || project.bedrooms.includes('6');
    }

    return matchesSearch && matchesStatus && matchesLocation && matchesPrice && matchesBedrooms;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters({ ...filters, [filterType]: value });
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      priceRange: 'all',
      bedrooms: 'all',
      location: 'all'
    });
    setSearchTerm('');
  };
  const getStatusColor = (status) => {
    switch (status) {
      case 'Ready to Move':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Under Construction':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Launching Soon':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-amber-700">Signature Projects</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Discover our collection of premium residential properties across prime locations
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search projects by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors"
            >
              <SlidersHorizontal size={20} />
              <span>Filters</span>
            </button>

            {/* Compare Button */}
            <Link
              to="/compare"
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-semibold transition-colors"
            >
              <GitCompare size={20} />
              <span>Compare</span>
            </Link>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 animate-fade-in">
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>
                        {status === 'all' ? 'All Status' : status}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Price Range</label>
                  <select
                    value={filters.priceRange}
                    onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                  >
                    <option value="all">All Prices</option>
                    <option value="under1m">Under $1M</option>
                    <option value="1m-2m">$1M - $2M</option>
                    <option value="2m-5m">$2M - $5M</option>
                    <option value="over5m">Over $5M</option>
                  </select>
                </div>

                {/* Bedrooms Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Bedrooms</label>
                  <select
                    value={filters.bedrooms}
                    onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                  >
                    <option value="all">All Configs</option>
                    <option value="1-2">1-2 BHK</option>
                    <option value="3-4">3-4 BHK</option>
                    <option value="5+">5+ BHK</option>
                  </select>
                </div>

                {/* Location Filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                  <select
                    value={filters.location}
                    onChange={(e) => handleFilterChange('location', e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all outline-none"
                  >
                    {locations.map(location => (
                      <option key={location} value={location}>
                        {location === 'all' ? 'All Locations' : location}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-bold text-amber-700">{filteredProjects.length}</span> of {projects.length} projects
                </div>
                <button
                  onClick={resetFilters}
                  className="text-sm text-amber-700 hover:text-amber-800 font-semibold"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
            >
              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`px-4 py-2 rounded-full text-xs font-semibold border ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>

                {/* Price Tag */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2">
                    <div className="text-xs text-gray-600 mb-1">Starting From</div>
                    <div className="text-xl font-bold text-gray-900">{project.price.split(' - ')[0]}</div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                  {project.name}
                </h3>
                <div className="flex items-center text-gray-600 mb-4">
                  <MapPin size={16} className="mr-2 text-amber-600" />
                  {project.mapLink ? (
                    <a
                      href={project.mapLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm hover:text-amber-600 transition-colors underline"
                    >
                      {project.location}
                    </a>
                  ) : (
                    <span className="text-sm">{project.location}</span>
                  )}
                </div>
                <p className="text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                  {project.description}
                </p>

                {/* Quick Info */}
                <div className="flex items-center space-x-4 mb-4 pb-4 border-b border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Home size={16} className="text-amber-600" />
                    <span className="text-sm text-gray-700 font-medium">{project.bedrooms}</span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{project.area}</span>
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to={`/project/${project.id}`}
                  className="group/link flex items-center justify-between text-amber-700 hover:text-amber-800 font-semibold transition-colors"
                >
                  <span>View Details</span>
                  <ArrowRight size={20} className="group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-gray-400 mb-4">
              <Search size={64} className="mx-auto" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={resetFilters}
              className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-br from-amber-50 via-white to-amber-50 rounded-2xl p-12 border border-amber-100">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Our team can help you find the perfect property that matches your requirements and budget.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-4 rounded-lg font-semibold inline-flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-amber-500/50"
            >
              <span>Talk to Our Experts</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
